import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import pako from "pako";

const { symbol } = window.coinCalculatorData || {};
const { cdnBaseUrl, cdnFormat } = window.xpayConfig || {
  cdnBaseUrl: "https://cdn.xpay.co/coins/webp/",
  cdnFormat: "webp",
};

// Helper function to generate CDN URL
const getCdnUrl = (coinSymbol) => {
  const base = cdnBaseUrl.replace(/\/$/, "");
  return `${base}/${coinSymbol?.toUpperCase()}.${cdnFormat}`;
};

const endPoint = "/wp-json/xpay/v1";
const endPointCache = "/wp-content/cache-api/symbols";
const ajaxUrl = '/wp-admin/admin-ajax.php';

// Fetch coin price from same AJAX endpoint as shortcodes (synced prices)
async function fetchCoinPrice(coinSymbol) {
  // First check if CoinPriceLoader already has this coin
  const upperSymbol = coinSymbol.toUpperCase();
  if (window.CoinPriceLoader?.state?.prices?.[upperSymbol]) {
    const coinPrice = window.CoinPriceLoader.state.prices[upperSymbol];
    return {
      symbol: upperSymbol,
      name: coinPrice.name,
      sellPrice: parseFloat(coinPrice.irr.replace(/,/g, '')),
      inDollarPrice: parseFloat(coinPrice.usd.replace(/,/g, '')),
    };
  }
  
  // Otherwise fetch from AJAX endpoint
  const nonce = window.xpayPriceLoader?.nonce || '';
  const response = await axios.post(ajaxUrl, new URLSearchParams({
    action: 'xpay_get_batch_prices',
    nonce: nonce,
    symbols: coinSymbol
  }));
  
  if (response.data?.success && response.data?.data?.coins?.[upperSymbol]) {
    const coinPrice = response.data.data.coins[upperSymbol];
    return {
      symbol: upperSymbol,
      name: coinPrice.name,
      sellPrice: parseFloat(coinPrice.irr.replace(/,/g, '')),
      inDollarPrice: parseFloat(coinPrice.usd.replace(/,/g, '')),
    };
  }
  
  throw new Error('Failed to fetch coin price');
}

// Fetch all currencies (for dropdown)
const fetchCurrencyData = async () => {
  const response = await axios.get(`${endPoint}/currency-data`);
  return response.data;
};

const CoinCalculator = () => {
  const [activeTab, setActiveTab] = useState("buy");
  const [searchText, setSearchText] = useState("");
  const [buyAmount, setBuyAmount] = useState("");
  const [sellAmount, setSellAmount] = useState("");
  const [buyOutput, setBuyOutput] = useState("");
  const [sellOutput, setSellOutput] = useState("");
  const [defaultCoin, setDefaultCoint] = useState([]);
  const [currentCoin, setCurrentCoin] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedBuyCoin, setSelectedBuyCoin] = useState(null);
  const [selectedSellCoin, setSelectedSellCoin] = useState(null);
  const [buyOptionsVisible, setBuyOptionsVisible] = useState(false);
  const [sellOptionsVisible, setSellOptionsVisible] = useState(false);
  const [activeBuyInput, setActiveBuyInput] = useState(null);
  const [activeSellInput, setActiveSellInput] = useState(null);
  const [dropdownDataLoaded, setDropdownDataLoaded] = useState(false);

  // Quick amount buttons data
  const buyQuickAmounts = [1000000, 5000000, 10000000, 20000000];

  // Amount text labels in Persian
  const buyAmountLabels = {
    1000000: "یک میلیون",
    5000000: "پنج میلیون",
    10000000: "ده میلیون",
    20000000: "بیست میلیون",
  };

  // Format number with Persian digits
  const formatPersianNumber = (number) => {
    const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    return number.toString().replace(/\d/g, (digit) => persianDigits[digit]);
  };

  // Format display text for buttons
  const formatButtonText = (amount) => {
    // Return Persian text if available, otherwise format as number
    return (
      buyAmountLabels[amount] ||
      amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "،")
    );
  };

  // Format number with separator based on value magnitude
  const formatNumberWithSeparator = useCallback((number) => {
    if (!number) return "";

    const numValue = parseFloat(number);
    if (isNaN(numValue)) return "";

    let formatted;
    const absValue = Math.abs(numValue);

    // Format based on magnitude for better readability
    if (absValue < 0.001) {
      // Very small numbers: 8 decimals
      formatted = numValue.toFixed(8).replace(/\.?0+$/, "");
    } else if (absValue < 0.01) {
      // Small numbers: 5 decimals
      formatted = numValue.toFixed(5).replace(/\.?0+$/, "");
    } else if (absValue < 0.1) {
      // Medium-small numbers: 4 decimals
      formatted = numValue.toFixed(4).replace(/\.?0+$/, "");
    } else if (absValue < 10) {
      // Medium numbers: 3 decimals
      formatted = numValue.toFixed(3).replace(/\.?0+$/, "");
    } else if (absValue < 1000) {
      // Large numbers: 2 decimals
      formatted = numValue.toFixed(2).replace(/\.?0+$/, "");
    } else if (absValue < 10000) {
      // Very large numbers: 1 decimal
      formatted = numValue.toFixed(1).replace(/\.?0+$/, "");
    } else {
      // Huge numbers: no decimals
      formatted = Math.round(numValue).toString();
    }

    const [integerPart, decimalPart] = formatted.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  }, []);

  const [coinData, setCoinData] = useState(null);
  const [isCoinLoading, setIsCoinLoading] = useState(true);

  // Listen to CoinPriceLoader event for synced prices
  useEffect(() => {
    const currentSymbol = (symbol || "BTC").toUpperCase();
    
    // Check if CoinPriceLoader already has the price
    const checkExistingPrice = () => {
      if (window.CoinPriceLoader?.state?.prices?.[currentSymbol]) {
        const coinPrice = window.CoinPriceLoader.state.prices[currentSymbol];
        console.log('[CoinCalculator] Using CoinPriceLoader data:', coinPrice.irr);
        
        // Convert to format expected by calculator
        const formattedData = {
          symbol: currentSymbol,
          name: coinPrice.name,
          sellPrice: parseFloat(coinPrice.irr.replace(/,/g, '')),
          inDollarPrice: parseFloat(coinPrice.usd.replace(/,/g, '')),
        };
        setCoinData(formattedData);
        setIsCoinLoading(false);
        return true;
      }
      return false;
    };

    // Try to get existing price first
    if (checkExistingPrice()) {
      return;
    }

    // Otherwise, wait for prices to load
    setIsCoinLoading(true);
    
    const handlePricesLoaded = (event) => {
      const coins = event.detail?.coins;
      if (coins?.[currentSymbol]) {
        const coinPrice = coins[currentSymbol];
        console.log('[CoinCalculator] Received price from event:', coinPrice.irr);
        
        const formattedData = {
          symbol: currentSymbol,
          name: coinPrice.name,
          sellPrice: parseFloat(coinPrice.irr.replace(/,/g, '')),
          inDollarPrice: parseFloat(coinPrice.usd.replace(/,/g, '')),
        };
        setCoinData(formattedData);
        setIsCoinLoading(false);
      }
    };

    document.addEventListener('xpay:pricesLoaded', handlePricesLoaded);
    
    // Cleanup
    return () => {
      document.removeEventListener('xpay:pricesLoaded', handlePricesLoaded);
    };
  }, [symbol]);

  // Fetch all currencies data only when dropdown is opened
  const {
    data: currenciesData,
    isError: isCurrenciesError,
    isSuccess: isCurrenciesSuccess,
    isLoading: isCurrenciesLoading,
    error: currenciesError,
  } = useQuery({
    queryKey: ["currenciesData"],
    queryFn: fetchCurrencyData,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    gcTime: 1000 * 60 * 30, // 30 minutes before garbage collection
    refetchOnWindowFocus: true,
    refetchInterval: false, // Disable automatic refetching
    enabled: dropdownDataLoaded, // Only fetch when dropdown is opened
  });

  // Combined loading state
  const isLoading = dropdownDataLoaded && isCurrenciesLoading;

  // Handle dropdown click to load currencies data
  const handleDropdownClick = useCallback(() => {
    if (!dropdownDataLoaded) {
      setDropdownDataLoaded(true);
    }
  }, [dropdownDataLoaded]);

  // Set currencies data when fetched
  useEffect(() => {
    if (isCurrenciesSuccess && currenciesData) {
      setCurrencies(currenciesData);
    }
  }, [isCurrenciesSuccess, currenciesData]);

  // Initialize with current coin data
  useEffect(() => {
    if (coinData) {
      setCurrentCoin(coinData);
      setSelectedBuyCoin(coinData);
      setSelectedSellCoin(coinData);

      setBuyAmount("");
      setBuyOutput("");

      setSellOutput("");
      setSellAmount("");
    }
  }, [coinData, formatNumberWithSeparator]);

  // Handle errors
  useEffect(() => {
    if (isCurrenciesError) {
      console.error("Error fetching currencies data:", currenciesError);
    }
  }, [isCurrenciesError, currenciesError]);

  // Handle buy input changes
  const handleBuyAmountChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Only allow numbers and one decimal point
      const cleanValue = value.replace(/,/g, "").replace(/[^0-9.]/g, "");

      // Prevent multiple decimal points
      const parts = cleanValue.split(".");
      const finalValue =
        parts.length > 2
          ? parts[0] + "." + parts.slice(1).join("")
          : cleanValue;

      setBuyAmount(finalValue);
      setActiveBuyInput("amount");

      if (selectedBuyCoin && finalValue !== "") {
        const convertedAmount =
          parseFloat(finalValue) * selectedBuyCoin.sellPrice;
        setBuyOutput(formatNumberWithSeparator(convertedAmount));
      } else {
        setBuyOutput("");
      }
    },
    [selectedBuyCoin, formatNumberWithSeparator],
  );

  // Handle buy output changes
  const handleBuyOutputChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Only allow numbers and decimal points
      if (/[a-zA-Z]/.test(value)) return;

      const cleanValue = value.replace(/,/g, "");
      setBuyOutput(formatNumberWithSeparator(cleanValue));

      setActiveBuyInput("output");

      if (selectedBuyCoin && cleanValue) {
        const convertedAmount =
          parseFloat(cleanValue) / selectedBuyCoin.sellPrice;
        setBuyAmount(formatNumberWithSeparator(convertedAmount));
      } else {
        setBuyAmount("");
      }
    },
    [selectedBuyCoin, formatNumberWithSeparator],
  );

  // Handle sell input changes
  const handleSellAmountChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Only allow numbers and decimal points
      if (/[a-zA-Z]/.test(value)) return;

      const cleanValue = value.replace(/,/g, "");

      setSellAmount(formatNumberWithSeparator(cleanValue));

      setActiveSellInput("amount");

      if (selectedSellCoin && cleanValue) {
        const convertedAmount =
          parseFloat(cleanValue) / selectedSellCoin.sellPrice;
        setSellOutput(formatNumberWithSeparator(convertedAmount));
      } else {
        setSellOutput("");
      }
    },
    [selectedSellCoin, formatNumberWithSeparator],
  );

  // Handle sell output changes
  const handleSellOutputChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Only allow numbers and one decimal point
      const cleanValue = value.replace(/,/g, "").replace(/[^0-9.]/g, "");

      // Prevent multiple decimal points
      const parts = cleanValue.split(".");
      const finalValue =
        parts.length > 2
          ? parts[0] + "." + parts.slice(1).join("")
          : cleanValue;

      setSellOutput(finalValue);

      setActiveSellInput("output");

      if (selectedSellCoin && finalValue) {
        const convertedAmount =
          parseFloat(finalValue) * selectedSellCoin.sellPrice;
        setSellAmount(formatNumberWithSeparator(convertedAmount));
      } else {
        setSellAmount("");
      }
    },
    [selectedSellCoin, formatNumberWithSeparator],
  );

  // Handle quick amount button click for buy (Toman amounts)
  const handleQuickAmountBuy = useCallback(
    (amount) => {
      setBuyOutput(formatNumberWithSeparator(amount));
      setActiveBuyInput("output");

      if (selectedBuyCoin) {
        const convertedAmount = parseFloat(amount) / selectedBuyCoin.sellPrice;
        setBuyAmount(formatNumberWithSeparator(convertedAmount));
      }
    },
    [selectedBuyCoin, formatNumberWithSeparator],
  );

  // Handle coin selection for buy
  const handleSelectBuyCoin = useCallback(
    (coin) => {
      setDefaultCoint(coin);
      setBuyOptionsVisible(false);
      setActiveBuyInput("amount");

      // Fetch new coin data from AJAX endpoint (synced with shortcodes)
      fetchCoinPrice(coin.symbol)
        .then((data) => {
          setSelectedBuyCoin(data);
          setCurrentCoin(data);

          setBuyAmount("");
          setBuyOutput("");
        })
        .catch((error) => {
          console.error("Error fetching coin data:", error);
          // Fallback to use dropdown coin data
          setSelectedBuyCoin(coin);
          setBuyAmount("");
          setBuyOutput("");
        });
    },
    [formatNumberWithSeparator],
  );

  // Handle coin selection for sell
  const handleSelectSellCoin = useCallback(
    (coin) => {
      setDefaultCoint(coin);
      setSellOptionsVisible(false);
      setActiveSellInput("amount");

      // Fetch new coin data from AJAX endpoint (synced with shortcodes)
      fetchCoinPrice(coin.symbol)
        .then((data) => {
          setSelectedSellCoin(data);
          setCurrentCoin(data);

          setSellOutput("");
          setSellAmount("");
        })
        .catch((error) => {
          console.error("Error fetching coin data:", error);
          // Fallback to use dropdown coin data
          setSelectedSellCoin(coin);
          setSellOutput("");
          setSellAmount("");
        });
    },
    [formatNumberWithSeparator],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buyOptionsVisible && !event.target.closest(".select-box")) {
        setBuyOptionsVisible(false);
      }
      if (sellOptionsVisible && !event.target.closest(".select-box")) {
        setSellOptionsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [buyOptionsVisible, sellOptionsVisible]);

  // Filter currencies based on search text
  const filteredCurrencies = useCallback(
    (searchText) => {
      if (!searchText.trim()) return currencies;

      const searchTerm = searchText.toLowerCase();
      return currencies.filter(
        (coin) =>
          coin.name.toLowerCase().includes(searchTerm) ||
          coin.symbol.toLowerCase().includes(searchTerm),
      );
    },
    [currencies],
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchText("");
  }, []);

  // Set initial values - removed to allow empty inputs
  useEffect(() => {
    // Allow inputs to remain empty for better UX
  }, [
    selectedBuyCoin,
    selectedSellCoin,
    buyAmount,
    buyOutput,
    sellAmount,
    sellOutput,
    formatNumberWithSeparator,
  ]);

  return (
    <div className="calculator">
      <ul className="flex align-center tab-links">
        <li
          className={`tab-link ${
            activeTab === "buy" ? "tab-link-active" : ""
          } flex align-center justify-center`}
          onClick={() => {
            setActiveTab("buy");
          }}
        >
          خرید
        </li>
        <li
          className={`tab-link ${
            activeTab === "sell" ? "tab-link-active" : ""
          } flex align-center justify-center`}
          onClick={() => {
            setActiveTab("sell");
          }}
        >
          فروش
        </li>
      </ul>
      <div className="tab-contents">
        {/* Buy Tab Content */}
        <div
          className={`tab-content ${
            activeTab === "buy" ? "tab-content-active" : ""
          }`}
        >
          <div
            id="currency-exchange-calculator-sell"
            className="currency-exchange-calculator flex column"
          >
            <div className="you-pay flex column">
              <p>پرداخت می کنید</p>
              <div className="flex align-center amount-box justify-bet">
                <input
                  type="text"
                  className="amount"
                  value={buyOutput}
                  onChange={handleBuyOutputChange}
                  placeholder="0.0"
                  aria-label="پرداخت می کنید"
                />
                <div className="ir-crr flex align-center">
                  <span>تومان</span>
                  <img
                    loading="lazy"
                    src="/wp-content/themes/xpay_main_theme/assets/img/ir.svg"
                    alt="IR-flag"
                  />
                </div>
              </div>
              <div className="quick-amounts flex align-center">
                {buyQuickAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => handleQuickAmountBuy(amount)}
                  >
                    <span>{formatButtonText(amount)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="output you-pay flex column">
              <p>دریافت می کنید</p>
              <div className="flex align-center amount-box justify-bet">
                <input
                  type="text"
                  id="output-amount-sell"
                  value={buyAmount}
                  onChange={handleBuyAmountChange}
                  placeholder="0.0"
                  aria-label="دریافت می کنید"
                />
                <div className="select-box">
                  <div
                    className="selected-option flex align-center justify-bet"
                    onClick={() => {
                      handleDropdownClick();
                      setBuyOptionsVisible(true);
                    }}
                  >
                    {selectedBuyCoin && (
                      <div>
                        <p>{selectedBuyCoin.symbol}</p>
                        <img
                          loading="lazy"
                          src={getCdnUrl(selectedBuyCoin.symbol)}
                          alt={`${selectedBuyCoin.symbol} image`}
                        />
                      </div>
                    )}
                  </div>
                  <div
                    className={`options-search flex column ${
                      buyOptionsVisible ? "activated-options" : ""
                    }`}
                  >
                    <div className="search-box flex align-center">
                      <svg
                        className="search"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <ellipse
                          cx="8.86276"
                          cy="8.4819"
                          rx="7.4819"
                          ry="7.4819"
                          stroke="#0086FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.0664 14.0742L16.9997 16.9999"
                          stroke="#0086FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <input
                        className="search-input"
                        type="text"
                        placeholder="نام یا نماد ارز مورد نظر را وارد نمایید"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      <span
                        className="close-options-search-btn"
                        onClick={() => {
                          clearSearch();
                          setBuyOptionsVisible(false);
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            opacity="0.08"
                            width="24"
                            height="24"
                            rx="8"
                            fill="#0086FF"
                          />
                          <path
                            d="M15.9956 8.00342L8.00391 15.9951"
                            stroke="#0086FF"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.9983 16L8 8"
                            stroke="#0086FF"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <span className="line"></span>
                    <div className="options flex column">
                      {filteredCurrencies(searchText).map((coin) => (
                        <div
                          key={coin.id}
                          className="option"
                          data-value={coin.symbol}
                          onClick={() => handleSelectBuyCoin(coin)}
                        >
                          <div>
                            {coin?.symbol && (
                              <img
                                loading="lazy"
                                src={getCdnUrl(coin.symbol)}
                                alt={`${coin.symbol}`}
                              />
                            )}
                            <div className="content flex column">
                              <p>{coin.name}</p>
                              <span>{coin.symbol}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="realtime-cost flex align-center justify-bet">
            <div className="flex align-center crr-named">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle opacity="0.1" cx="10" cy="10" r="10" fill="#0086FF" />
                <circle cx="10" cy="10" r="4" fill="#0086FF" />
              </svg>
              <p className="coin-name-buy">
                {selectedBuyCoin ? `قیمت روز ${selectedBuyCoin.name}:` : ""}
              </p>
            </div>
            <span className={`realtime-value-buy xpay-price ${isCoinLoading ? 'skeleton-loading' : ''}`}>
              {isCoinLoading ? "---" : selectedBuyCoin ? `${formatNumberWithSeparator(selectedBuyCoin.sellPrice)} تومان` : ""}
            </span>
          </div>
          <a
            href="https://app.xpay.co/login"
            rel="nofollow"
            className="flex align-center justify-center buy-crr"
          >
            {selectedBuyCoin ? `معامله ${selectedBuyCoin.name}` : ""}
          </a>
        </div>

        {/* Sell Tab Content */}
        <div
          className={`tab-content ${
            activeTab === "sell" ? "tab-content-active" : ""
          }`}
        >
          <div
            id="currency-exchange-calculator-buy"
            className="currency-exchange-calculator flex column"
          >
            <div className="you-pay flex column">
              <p>پرداخت می کنید</p>
              <div className="flex align-center amount-box justify-bet">
                <input
                  type="text"
                  className="amount"
                  value={sellOutput}
                  onChange={handleSellOutputChange}
                  placeholder="0.0"
                  aria-label="پرداخت می کنید"
                />
                <div className="select-box">
                  <div
                    className="selected-option flex align-center justify-bet"
                    onClick={() => {
                      handleDropdownClick();
                      setSellOptionsVisible(true);
                    }}
                  >
                    {selectedSellCoin && (
                      <div>
                        <p>{selectedSellCoin.symbol}</p>
                        <img
                          loading="lazy"
                          src={getCdnUrl(selectedSellCoin.symbol)}
                          alt={`${selectedSellCoin.symbol} image`}
                        />
                      </div>
                    )}
                  </div>
                  <div
                    className={`options-search flex column ${
                      sellOptionsVisible ? "activated-options" : ""
                    }`}
                  >
                    <div className="search-box flex align-center">
                      <svg
                        className="search"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <ellipse
                          cx="8.86276"
                          cy="8.4819"
                          rx="7.4819"
                          ry="7.4819"
                          stroke="#0086FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.0664 14.0742L16.9997 16.9999"
                          stroke="#0086FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <input
                        className="search-input"
                        type="text"
                        placeholder="نام یا نماد ارز مورد نظر را وارد نمایید"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      <span
                        className="close-options-search-btn"
                        onClick={() => {
                          clearSearch();
                          setSellOptionsVisible(false);
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            opacity="0.08"
                            width="24"
                            height="24"
                            rx="8"
                            fill="#0086FF"
                          />
                          <path
                            d="M15.9956 8.00342L8.00391 15.9951"
                            stroke="#0086FF"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.9983 16L8 8"
                            stroke="#0086FF"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <span className="line"></span>
                    <div className="options flex column">
                      {filteredCurrencies(searchText).map((coin) => (
                        <div
                          key={coin.id}
                          className="option"
                          data-value={coin.symbol}
                          onClick={() => handleSelectSellCoin(coin)}
                        >
                          <div>
                            {coin?.symbol && (
                              <img
                                loading="lazy"
                                src={getCdnUrl(coin.symbol)}
                                alt={`${coin.symbol} image`}
                              />
                            )}
                            <div className="content flex column">
                              <p>{coin.name}</p>
                              <span>{coin.symbol}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="output you-pay flex column">
              <p>دریافت می کنید</p>
              <div className="flex align-center amount-box justify-bet">
                <input
                  type="text"
                  id="output-amount-buy"
                  value={sellAmount}
                  onChange={handleSellAmountChange}
                  placeholder="0.0"
                  aria-label="دریافت می کنید"
                />
                <div className="ir-crr flex align-center">
                  <span>تومان</span>
                  <img
                    loading="lazy"
                    src="/wp-content/themes/xpay_main_theme/assets/img/ir.svg"
                    alt="IR-flag"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="realtime-cost flex align-center justify-bet">
            <div className="flex align-center crr-named">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle opacity="0.1" cx="10" cy="10" r="10" fill="#0086FF" />
                <circle cx="10" cy="10" r="4" fill="#0086FF" />
              </svg>
              <p className="coin-name-sell">
                {selectedSellCoin ? `قیمت روز ${selectedSellCoin.name}:` : ""}
              </p>
            </div>
            <span className={`realtime-value-sell xpay-price ${isCoinLoading ? 'skeleton-loading' : ''}`}>
              {isCoinLoading ? "---" : selectedSellCoin ? `${formatNumberWithSeparator(selectedSellCoin.sellPrice)} تومان` : ""}
            </span>
          </div>
          <a
            href="https://app.xpay.co/login"
            rel="nofollow"
            className="flex align-center justify-center buy-crr"
          >
            {selectedSellCoin ? `معامله ${selectedSellCoin.name}` : ""}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CoinCalculator;
